import React, { useEffect, useState } from 'react';
import {
  Paper, Typography, Box, TextField, MenuItem, Button, Checkbox,
  FormControlLabel, Alert, CircularProgress, Table, TableHead, TableRow,
  TableCell, TableBody, Card, CardContent, Divider, Grid, Chip, IconButton,
  Snackbar, Tabs, Tab, TextareaAutosize, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { VpnKey, Search as SearchIcon, Refresh as RefreshIcon, ContentPaste, ShoppingCart } from '@mui/icons-material';
import api from '../../services/api';
import { useSearchParams } from 'react-router-dom';

const RUBROS = ['', 'TI', 'Obras', 'Mantención', 'Limpieza', 'Transporte', 'Otros'];
const REGIONES = ['', 'II Tarapacá', 'III Antofagasta', 'IV Atacama', 'IV Coquimbo',
  'V Valparaíso', 'RM Metropolitana', 'VI O’Higgins', 'VII Maule', 'VIII Biobío',
  'IX La Araucanía', 'XIV Los Ríos', 'X Los Lagos', 'XI Aysén', 'XII Magallanes', 'XV Ñuble'];

function fmtTs(v) {
  return v ? new Date(v * 1000).toLocaleString('es-CL') : '—';
}

export default function MPLicitacionesPage() {
  const [searchParams] = useSearchParams();
  const initTab = (() => {
    const v = parseInt(searchParams.get('tab') || '0', 10);
    return Number.isFinite(v) && v >= 0 && v <= 5 ? v : 0;
  })();
  const [tab, setTab] = useState(initTab);
  const [status, setStatus] = useState(null);
  const [verify, setVerify] = useState(null);
  const [form, setForm] = useState({
    keywords: '', rubro: '', zona: '', fecha_desde: '', fecha_hasta: '',
    guardar: false, max_resultados: 50,
  });
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState('');
  const [cookieRaw, setCookieRaw] = useState('');
  const [importBusy, setImportBusy] = useState(false);
  const [caItems, setCaItems] = useState(null);
  const [caBusy, setCaBusy] = useState(false);
  const [caMsg, setCaMsg] = useState('');
  const [caSearch, setCaSearch] = useState('');
  const [caEstado, setCaEstado] = useState(2);
  const [caPage, setCaPage] = useState(1);
  const [caMeta, setCaMeta] = useState(null);
  const [caEstados, setCaEstados] = useState([]);
  const [ocItems, setOcItems] = useState(null);
  const [ocBusy, setOcBusy] = useState(false);
  const [ocMsg, setOcMsg] = useState('');
  const [ocSearch, setOcSearch] = useState('');
  const [ocPage, setOcPage] = useState(1);
  const [ocMeta, setOcMeta] = useState(null);
  const [health, setHealth] = useState(null);
  const [healthBusy, setHealthBusy] = useState(false);
  const [postItems, setPostItems] = useState(null);
  const [postBusy, setPostBusy] = useState(false);
  const [postDialog, setPostDialog] = useState(null); // {codigo,tipo,nombre,organismo}
  const [postFolder, setPostFolder] = useState('');
  const [postFolders, setPostFolders] = useState([]);
  const [postMsg, setPostMsg] = useState('');
  const [postDetail, setPostDetail] = useState(null); // ultima postulacion creada/detalle

  const loadStatus = async () => {
    try { setStatus((await api.get('/mp-portal/status')).data); } catch (e) { setStatus(null); }
  };

  useEffect(() => { loadStatus(); }, []);
  useEffect(() => { if (tab === 4) { doPostulaciones(); } }, [tab]);

  const doVerify = async () => {
    setVerifying(true);
    setVerify(null);
    try {
      const { data } = await api.post('/mp-portal/verify-session');
      setVerify(data);
      setSnack(data.alive ? 'Sesión de Mercado Público VIVA — conectada con tu cookie' : 'Sesión NO viva (cookie sin sesión o expirada)');
    } catch (e) {
      setVerify({ alive: false, stdout_tail: [], exit: -1, error: String(e) });
      setSnack('Error al verificar sesión');
    } finally {
      setVerifying(false);
      loadStatus();
    }
  };

  const doImport = async () => {
    setImportBusy(true);
    try {
      const { data } = await api.post('/mp-portal/import-cookie', { raw: cookieRaw });
      setSnack(data.ok ? `Cookie importada: ${data.count} cookies (${(data.domains || []).join(', ').slice(0, 90)})` : `Error: ${data.error}`);
      setCookieRaw('');
      loadStatus();
    } catch (e) {
      setSnack('Error de importación: ' + (e?.response?.data?.detail || String(e)));
    } finally {
      setImportBusy(false);
    }
  };

  const doSearch = async () => {
    setBusy(true);
    setError('');
    try {
      const body = {
        ...form,
        fecha_desde: form.fecha_desde || null,
        fecha_hasta: form.fecha_hasta || null,
        max_resultados: Number(form.max_resultados) || 50,
      };
      const { data: res } = await api.post('/mp-portal/search', body);
      setResults(res);
      if (!res.ok) setError(res.error || 'Búsqueda no disponible');
      else setSnack(`Búsqueda OK: ${res.guardados ?? 0} guardados`);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error de comunicación con el backend');
    } finally {
      setBusy(false);
    }
  };

  const loadCaEstados = async () => {
    if (caEstados.length > 0) return;
    try {
      const { data } = await api.get('/mp-portal/compras-agiles/estados');
      const ee = (data.estados || []).filter((x) => x && x.id != null && x.nombre);
      if (data.ok && ee.length) setCaEstados(ee);
    } catch (e) { /* silent */ }
  };

  const doComprasAgiles = async (page = 1) => {
    setCaBusy(true);
    setCaMsg('');
    try {
      const params = { estado: caEstado, page };
      if (caSearch.trim()) params.keywords = caSearch.trim();
      const { data } = await api.get('/mp-portal/compras-agiles', { params });
      if (!data.ok) {
        setCaItems([]);
        setCaMeta(null);
        setCaMsg(data.error || 'No disponible');
      } else {
        setCaItems(page > 1 ? [...(caItems || []), ...(data.items || [])] : (data.items || []));
        setCaPage(page);
        setCaMeta({ total: data.total || 0, pages: data.pages || 0, page_size: data.page_size || 15 });
        if (!(data.items || []).length) setCaMsg('Sin resultados');
      }
    } catch (e) {
      setCaMsg(e?.response?.data?.detail || 'Error de comunicación');
    } finally {
      setCaBusy(false);
    }
  };

  const doOrdenes = async (page = 1) => {
    setOcBusy(true);
    setOcMsg('');
    try {
      const params = { page };
      if (ocSearch.trim()) params.keywords = ocSearch.trim();
      const { data } = await api.get('/mp-portal/ordenes-de-compra', { params });
      if (!data.ok) {
        setOcItems([]);
        setOcMeta(null);
        setOcMsg(data.error || 'No disponible');
      } else {
        setOcItems(page > 1 ? [...(ocItems || []), ...(data.items || [])] : (data.items || []));
        setOcPage(page);
        setOcMeta({ total: data.total || 0, pages: data.pages || 0 });
      }
    } catch (e) {
      setOcMsg(e?.response?.data?.detail || 'Error de comunicación');
    } finally {
      setOcBusy(false);
    }
  };

  const doHealth = async () => {
    setHealthBusy(true);
    setHealth(null);
    try {
      const { data } = await api.get('/mp-portal/health-check');
      setHealth(data);
    } catch (e) {
      setHealth({ ready: false, err: String(e) });
    } finally {
      setHealthBusy(false);
    }
  };

  const loadPostFolders = async () => {
    try {
      const { data } = await api.get('/documentos/folders');
      const items = data.items || data.folders || [];
      setPostFolders(items.map((f) => ({ id: f.id, name: f.name })));
    } catch (e) { /* silent */ }
  };

  const openPostDialog = (row, tipo) => {
    setPostDialog({ codigo: row.codigo || row.CodigoExterno || String(row.id || ''), tipo, nombre: row.nombre || '', organismo: row.organismo || row.Comprador || '' });
    setPostMsg('');
    setPostDetail(null);
    setPostFolder('');
    loadPostFolders();
  };

  const createPostulacion = async () => {
    if (!postDialog || !postFolder) { setPostMsg('Seleccione una carpeta de Documentos'); return; }
    setPostBusy(true);
    setPostMsg('');
    try {
      const { data } = await api.post('/mp-portal/postulaciones', {
        codigo: postDialog.codigo, tipo: postDialog.tipo, nombre: postDialog.nombre,
        organismo: postDialog.organismo, folder_id: postFolder,
      });
      if (!data.ok) setPostMsg(data.error || 'No se pudo crear');
      else {
        setPostDetail(data.postulacion);
        setSnack(data.reused ? 'La postulacion ya existia y se va a reutilizar' : 'Postulacion creada: suba documentos a la carpeta y procese con IA');
        setPostDialog(null);
      }
    } catch (e) {
      setPostMsg(e?.response?.data?.detail || 'Error comunicacion');
    } finally {
      setPostBusy(false);
    }
  };

  const doPostulaciones = async () => {
    setPostBusy(true);
    try {
      const { data } = await api.get('/mp-portal/postulaciones');
      setPostItems(data.items || []);
    } catch (e) {
      setPostItems([]);
    } finally {
      setPostBusy(false);
    }
  };

  const procesarPost = async (id) => {
    setPostBusy(true);
    try {
      const { data } = await api.post(`/mp-portal/postulaciones/${id}/procesar`);
      setSnack(data.ok ? 'Procesando con IA (opencode)... consulte estado en unos segundos' : (data.error || 'No se pudo procesar'));
      doPostulaciones();
    } catch (e) {
      setSnack('Error procesando');
    } finally {
      setPostBusy(false);
    }
  };

  const enviarPost = async (id) => {
    setPostBusy(true);
    try {
      const { data } = await api.post(`/mp-portal/postulaciones/${id}/enviar`);
      if (!data.ok) setPostMsg(data.error || 'No enviado');
      else setPostMsg(data.msg || 'Paquete listo');
    } catch (e) {
      setPostMsg('Error envio');
    } finally {
      setPostBusy(false);
    }
  };

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>MP Licitaciones (ClaveÚnica)</Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <VpnKey color="primary" />
            <Typography variant="h6">Estado de sesión</Typography>
            <IconButton onClick={loadStatus} size="small"><RefreshIcon /></IconButton>
          </Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            storage_state importado: {fmtTs(status?.storage_created_at)} · intentos: {status?.worker_state?.last?.status || 'ninguno'} · RESET.flag: {status?.reset_flag ? 'presente' : 'no'}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
            "Verificar sesión" carga las cookies importadas en un navegador real y comprueba la sesión contra mercadopublico.cl.
          </Typography>
          <Button variant="outlined" onClick={doVerify} disabled={verifying} sx={{ mr: 1 }}>
            {verifying ? <CircularProgress size={20} /> : 'Verificar sesión'}
          </Button>
          <Button variant="outlined" color="secondary" onClick={doHealth} disabled={healthBusy}>
            {healthBusy ? <CircularProgress size={20} /> : 'Health check'}
          </Button>
          {health && (
            <Box sx={{ mt: 1 }}>
              <Chip size="small" label={health.ready ? 'MP-APIS OK' : 'MP-APIS FALLO'} color={health.ready ? 'success' : 'error'} sx={{ mr: 1 }} />
              {Object.entries(health.buscador || {}).map(([k, v]) => (
                <Chip key={k} size="small" label={`${k}: ${v}`} color={String(v) === 'ok' ? 'default' : 'warning'} sx={{ mr: 0.5, mb: 0.5 }} />
              ))}
              {health.opencode && health.opencode.url && (
                <Chip size="small" label={`opencode: ${health.opencode.ok ? 'ok' : health.opencode.err || 'fallo'}`} color={health.opencode.ok ? 'default' : 'warning'} sx={{ mr: 0.5, mb: 0.5 }} />
              )}
            </Box>
          )}
          {verify && (
            <Box sx={{ mt: 1 }}>
              <Chip size="small" label={verify.alive ? 'SESIÓN VIVA' : 'SESIÓN NO VIVA'} color={verify.alive ? 'success' : 'error'} />
              {!!(verify.stdout_tail?.length) && (
                <pre style={{ fontSize: '0.74rem', background: '#f5f5f5', padding: 8, marginTop: 8, overflowX: 'auto' }}>
                  {verify.stdout_tail.join('\n')}
                </pre>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)}>
          <Tab label="Búsqueda" />
          <Tab label="Compras Ágiles" icon={<ShoppingCart fontSize="small" />} />
          <Tab label="Órdenes de Compra" />
          <Tab label="Trato Directo" />
          <Tab label="Postulaciones" />
          <Tab label="Importar cookie" icon={<ContentPaste fontSize="small" />} />
        </Tabs>
        <Divider />

        {tab === 0 && (
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Palabras clave" value={form.keywords} onChange={upd('keywords')} />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField select fullWidth label="Rubro" value={form.rubro} onChange={upd('rubro')}>
                  {RUBROS.map((r) => <MenuItem key={r} value={r}>{r || 'Todos'}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField select fullWidth label="Región" value={form.zona} onChange={upd('zona')}>
                  {REGIONES.map((r) => <MenuItem key={r} value={r}>{r || 'Todas'}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField fullWidth label="Máx. resultados" type="number" value={form.max_resultados} onChange={upd('max_resultados')} inputProps={{ min: 1, max: 150 }} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField fullWidth label="Fecha desde" type="date" value={form.fecha_desde} onChange={upd('fecha_desde')} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField fullWidth label="Fecha hasta" type="date" value={form.fecha_hasta} onChange={upd('fecha_hasta')} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel control={<Checkbox checked={form.guardar} onChange={upd('guardar')} />} label="Guardar resultados en BD" />
              </Grid>
              <Grid item xs={12} sx={{ textAlign: 'right' }}>
                <Button variant="contained" startIcon={<SearchIcon />} onClick={doSearch} disabled={busy}>
                  {busy ? <CircularProgress size={20} /> : 'Buscar en Mercado Público'}
                </Button>
              </Grid>
            </Grid>
            {error && <Alert severity="warning" sx={{ mt: 2 }}>{error}</Alert>}
            {results && results.ok && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6">Resultados ({results.resultados?.length ?? 0}) · guardados: {results.guardados ?? 0}</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Código</TableCell><TableCell>Nombre</TableCell><TableCell>Organismo</TableCell><TableCell>Cierre</TableCell><TableCell>Monto</TableCell><TableCell>Postular</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(results.resultados || []).map((r, i) => (
                      <TableRow key={r.codigo || r.CodigoExterno || i}>
                        <TableCell>{r.codigo || r.CodigoExterno}</TableCell>
                        <TableCell>{r.nombre || r.Nombre}</TableCell>
                        <TableCell>{r.organismo || r.Comprador}</TableCell>
                        <TableCell>{r.fecha_cierre || r.FechaCierre}</TableCell>
                        <TableCell>{r.monto_estimado ?? r.MontoEstimado}</TableCell>
                        <TableCell>
                          <Button size="small" variant="outlined" onClick={() => openPostDialog(r, 'licitacion')}>Postular</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Buscador Compra Ágil de Mercado Público (API pública, sin cookie).
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label="Palabras clave (opcional)"
                  value={caSearch}
                  onChange={(e) => setCaSearch(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter') { loadCaEstados(); doComprasAgiles(1); } }}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  select
                  fullWidth
                  label="Estado"
                  value={caEstado}
                  onChange={(e) => setCaEstado(e.target.value)}
                >
                  {(caEstados.length ? caEstados : [{ id: 2, nombre: 'Publicada' }, { id: 3, nombre: 'Adjudicada' }, { id: 4, nombre: 'Cerrada' }, { id: 8, nombre: 'Desierta' }]).map((st) => (
                    <MenuItem key={st.id} value={st.id}>{st.nombre}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  fullWidth
                  onClick={() => { loadCaEstados(); doComprasAgiles(1); }}
                  disabled={caBusy}
                >
                  {caBusy ? <CircularProgress size={20} /> : 'Buscar Compras Ágiles'}
                </Button>
              </Grid>
            </Grid>
            {caMsg && <Alert severity="info" sx={{ mt: 2 }}>{caMsg}</Alert>}
            {caMeta && (
              <Typography variant="body2" sx={{ mt: 2 }}>
                Total: {caMeta.total} · Página {caPage} de {caMeta.pages}
              </Typography>
            )}
            {caItems && caItems.length > 0 && (
              <Table size="small" sx={{ mt: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell><TableCell>Nombre</TableCell><TableCell>Organismo</TableCell>
                    <TableCell>Estado</TableCell><TableCell>Publicación</TableCell><TableCell>Cierre</TableCell>
                    <TableCell>Monto</TableCell><TableCell>Cotiz.</TableCell><TableCell>Postular</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {caItems.map((it, i) => (
                    <TableRow key={it.id || i}>
                      <TableCell>{it.codigo}</TableCell>
                      <TableCell>{it.nombre}</TableCell>
                      <TableCell>{it.organismo}{it.unidad ? ` · ${it.unidad}` : ''}</TableCell>
                      <TableCell>{it.estado}</TableCell>
                      <TableCell>{it.fecha_publicacion}</TableCell>
                      <TableCell>{it.fecha_cierre}</TableCell>
                      <TableCell>{it.monto_disponible != null ? Number(it.monto_disponible).toLocaleString('es-CL') : '—'} {it.moneda || 'CLP'}</TableCell>
                      <TableCell>{it.cantidad_proveedores_cotizando ?? 0}</TableCell>
                      <TableCell>
                        <Button size="small" variant="outlined" onClick={() => openPostDialog(it, 'compra-agil')}>Postular</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {caMeta && caPage < caMeta.pages && (
              <Button sx={{ mt: 2 }} variant="outlined" onClick={() => doComprasAgiles(caPage + 1)} disabled={caBusy}>
                {caBusy ? <CircularProgress size={20} /> : `Cargar más (página ${caPage + 1})`}
              </Button>
            )}
          </Box>
        )}

        {tab === 2 && (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Órdenes de compra del Buscador Mercado Público (API pública, sin cookie).
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Palabras clave (opcional)"
                  value={ocSearch}
                  onChange={(e) => setOcSearch(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter') doOrdenes(1); }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button variant="contained" startIcon={<SearchIcon />} fullWidth onClick={() => doOrdenes(1)} disabled={ocBusy}>
                  {ocBusy ? <CircularProgress size={20} /> : 'Buscar Órdenes'}
                </Button>
              </Grid>
            </Grid>
            {ocMsg && <Alert severity="info" sx={{ mt: 2 }}>{ocMsg}</Alert>}
            {ocMeta && (
              <Typography variant="body2" sx={{ mt: 2 }}>
                Total: {ocMeta.total} · Página {ocPage} de {ocMeta.pages}
              </Typography>
            )}
            {ocItems && ocItems.length > 0 && (
              <Table size="small" sx={{ mt: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell><TableCell>Descripción</TableCell><TableCell>Comprador</TableCell>
                    <TableCell>Proveedor</TableCell><TableCell>Estado</TableCell><TableCell>Fecha envío</TableCell><TableCell>Monto</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ocItems.map((it, i) => (
                    <TableRow key={it.id || i}>
                      <TableCell>{it.codigo}</TableCell>
                      <TableCell>{it.nombre}</TableCell>
                      <TableCell>{it.nombre_organismo_comprador}</TableCell>
                      <TableCell>{it.nombre_proveedor}</TableCell>
                      <TableCell>{it.estado}</TableCell>
                      <TableCell>{it.fecha_envio}</TableCell>
                      <TableCell>{it.monto_total != null ? Number(it.monto_total).toLocaleString('es-CL') : '—'} {it.moneda || 'CLP'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {ocMeta && ocPage < ocMeta.pages && (
              <Button sx={{ mt: 2 }} variant="outlined" onClick={() => doOrdenes(ocPage + 1)} disabled={ocBusy}>
                {ocBusy ? <CircularProgress size={20} /> : `Cargar más (página ${ocPage + 1})`}
              </Button>
            )}
          </Box>
        )}

        {tab === 3 && (
          <Box sx={{ p: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Módulos adicionales de Mercado Público.
            </Alert>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Módulo</TableCell><TableCell>Estado</TableCell><TableCell>Enlace oficial</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Licitaciones (Standard y Beta)</TableCell>
                  <TableCell>Búsqueda estándar integrada (pestaña Búsqueda) · Beta: en exploración</TableCell>
                  <TableCell><a href="https://www.mercadopublico.cl/BusquedaLicitaciones" target="_blank" rel="noreferrer">Buscador MP</a></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Subasta Inversa</TableCell>
                  <TableCell>En exploración (endpoint público pendiente)</TableCell>
                  <TableCell><a href="https://subastainversa.mercadopublico.cl/" target="_blank" rel="noreferrer">subastainversa.mercadopublico.cl</a></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Trato Directo (Marketplace / Beta)</TableCell>
                  <TableCell>SPA detectada; listado público pendiente de mapeo</TableCell>
                  <TableCell><a href="https://trato-directo.mercadopublico.cl/" target="_blank" rel="noreferrer">trato-directo.mercadopublico.cl</a></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        )}

        {tab === 4 && (
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h6">Mis postulaciones</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Flujo: cree postulacion desde Búsqueda/Compra Ágil -> suba los documentos de la licitacion a la carpeta:
                  procese con IA -> revise y envie el paquete.
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button variant="contained" onClick={doPostulaciones} disabled={postBusy} fullWidth>
                  {postBusy ? <CircularProgress size={20} /> : 'Refrescar'}
                </Button>
              </Grid>
            </Grid>
            {postItems !== null && postItems.length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>Sin postulaciones: use el boton Postular en la tabla de resultados.</Alert>
            )}
            {postItems && postItems.length > 0 && (
              <Table size="small" sx={{ mt: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell><TableCell>Tipo</TableCell><TableCell>Estado</TableCell><TableCell>Carpeta</TableCell><TableCell>Archivos</TableCell><TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {postItems.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>{p.codigo}</TableCell>
                      <TableCell>{p.tipo}</TableCell>
                      <TableCell>
                        <Chip size="small" label={p.estado} color={p.estado === 'listo' ? 'success' : p.estado === 'error' ? 'error' : 'default'} />
                        {p.run?.status && <Typography variant="caption" sx={{ display: 'block' }}>run: {p.run.status}</Typography>}
                      </TableCell>
                      <TableCell>{p.folder_id ? p.folder_id.slice(0, 8) + '…' : '—'}</TableCell>
                      <TableCell>{(p.archivos || []).length}</TableCell>
                      <TableCell>
                        <Button size="small" variant="outlined" sx={{ mr: 0.5 }} onClick={() => procesarPost(p.id)} disabled={postBusy || p.estado === 'procesando' || p.estado === 'listo'}>Procesar IA</Button>
                        <Button size="small" variant="outlined" sx={{ mr: 0.5 }} onClick={() => setPostDetail(p)}>Ver resultado</Button>
                        <Button size="small" variant="text" onClick={() => enviarPost(p.id)} disabled={postBusy || p.estado !== 'listo'}>Enviar</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        )}

        {tab === 5 && (
          <Box sx={{ p: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              1. Inicia sesión manualmente en mercadopublico.cl en TU navegador. 2. Instala la extensión
              "Cookie-Editor" (Chrome/Edge/Firefox). 3. En la pestaña de mercadopublico, con Cookie-Editor,
              exporta (Export) y pega ese JSON aquí. 4. REPITE lo mismo dentro de heimdall.mercadopublico.cl
              (la pantalla de login usa ese dominio): puedes pegar ambas exportaciones una tras otra en el mismo
              cuadro de texto. 5. Pulsa "Importar cookie" y luego "Verificar sesión".
            </Alert>
            <TextareaAutosize
              minRows={8}
              style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.8rem', padding: 10, border: '1px solid #ccc', borderRadius: 6 }}
              placeholder='Pega aquí el JSON exportado de Cookie-Editor (www. mercadopublico.cl y heimdall.mercadopublico.cl)'
              value={cookieRaw}
              onChange={(e) => setCookieRaw(e.target.value)}
            />
            <Box sx={{ mt: 2, textAlign: 'right' }}>
              <Button variant="contained" onClick={doImport} disabled={importBusy || cookieRaw.trim().length < 10}>
                {importBusy ? <CircularProgress size={20} /> : 'Importar cookie'}
              </Button>
            </Box>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
              Las cookies se guardan en el servidor con permisos 600 y se usan SOLO para navegar con tu sesión; el
              módulo consultará y guardará únicamente lo que tú pidas (sin scraping masivo).
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Dialog crear postulacion */}
      <Dialog open={!!postDialog} onClose={() => setPostDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Nueva postulación {postDialog?.tipo === 'compra-agil' ? 'Compra Ágil' : 'Licitación'}: {postDialog?.codigo}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            1) Descargue de Mercado Público las bases/anexos. 2) Súbalos a una carpeta de Documentos.
            3) Seleccione aquí la carpeta: la IA creará el paquete de respuesta.
          </Alert>
          <TextField
            select
            fullWidth
            label="Carpeta de Documentos"
            value={postFolder}
            onChange={(e) => setPostFolder(e.target.value)}
            sx={{ mb: 2 }}
          >
            <MenuItem value="">— Seleccione —</MenuItem>
            {postFolders.map((f) => (
              <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
            ))}
          </TextField>
          {postMsg && <Alert severity="warning">{postMsg}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPostDialog(null)}>Cancelar</Button>
          <Button variant="contained" onClick={createPostulacion} disabled={postBusy}>
            {postBusy ? <CircularProgress size={18} /> : 'Crear postulación'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog detalle postulacion */}
      <Dialog open={!!postDetail} onClose={() => setPostDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>Postulación {postDetail?.codigo} · {postDetail?.estado}</DialogTitle>
        <DialogContent>
          {(postDetail?.archivos || []).length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Documentos subidos:</Typography>
              <ul style={{ marginTop: 4 }}>
                {postDetail.archivos.map((a) => (
                  <li key={a.path} style={{ fontSize: '0.8rem' }}>
                    <a href={'/uploads' + a.path.replace('/app/uploads', '')} target="_blank" rel="noreferrer">{a.name}</a> ({Math.round((a.size || 0) / 1024)} KB)
                  </li>
                ))}
              </ul>
            </>
          )}
          {(postDetail?.run?.generated_files || []).length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Paquete generado por IA:</Typography>
              <ul style={{ marginTop: 4 }}>
                {postDetail.run.generated_files.map((g, i) => {
                  const path = typeof g === 'string' ? g : (g.path || g.name || '');
                  const name = typeof g === 'string' ? g.split('/').pop() : (g.name || path.split('/').pop());
                  return (
                    <li key={i} style={{ fontSize: '0.8rem' }}>
                      <a href={'/uploads' + String(path).replace('/app/uploads', '')} target="_blank" rel="noreferrer">{name}</a>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
          {postDetail?.run?.result && (
            <pre style={{ fontSize: '0.72rem', background: '#f7f7f7', padding: 10, marginTop: 10, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>
              {String(postDetail.run.result).slice(0, 6000)}
            </pre>
          )}
          {postDetail?.error && <Alert severity="error" sx={{ mt: 1 }}>{postDetail.error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPostDetail(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={5000} onClose={() => setSnack('')} message={snack} />
    </Box>
  );
}
